/**
 * ActiveFilters — die aktiven Library-Filter OHNE eigenen Facetten-Chip
 * (Freitext-Suche, „Ohne Tags", „Meine Uploads") als entfernbare Chips +
 * „Alle Filter zurücksetzen".
 *
 * Seit Redesign S2 Phase 2a (2026-10-10) ist das kein eigenes Band mehr: die
 * Facetten (Typ · Produkt & Kategorie · Verwendung · Tags · Status) zeigen ihren
 * aktiven Zustand selbst als grüner Chip mit Anzahl + × (`LibraryFilterChips`).
 * Hier bleibt, was keine Facette hat — v. a. der sonst unsichtbare `?q=`
 * (IA Befund 1). Rendert ein Fragment → die Chips fließen in die Chip-Zeile.
 * Sort/dir + View erscheinen NICHT (Präsentation, kein Filter).
 *
 * Rendert `null`, wenn nichts davon aktiv ist und kein Filter gesetzt ist.
 */
import { I } from '../lib/icons';
import { FILTER_CHIP_ACTIVE_LABEL, FILTER_CHIP_CLEAR } from './ui/filter-chip';

type Props = {
  /** Freitext `?q=` (`''` = inaktiv). */
  q: string;
  /** Schnellzugriff-Boolean-Filter (Dashboard-Triage-Karten). */
  untagged: boolean;
  /**
   * „Meine Uploads" als Chip — der Aufrufer reicht `false` durch, wenn der
   * Ansichten-Tab „Meine Uploads" den Filter schon sichtbar macht.
   */
  mine: boolean;
  /** Mind. ein Filter aktiv (inkl. Facetten) → „Alle Filter zurücksetzen". */
  anyActive: boolean;

  onClearQuery: () => void;
  onClearUntagged: () => void;
  onClearMine: () => void;
  onClearAll: () => void;
};

type Chip = { id: string; label: string; onRemove: () => void };

export function ActiveFilters({
  q,
  untagged,
  mine,
  anyActive,
  onClearQuery,
  onClearUntagged,
  onClearMine,
  onClearAll,
}: Props) {
  const chips: Chip[] = [];
  if (q) chips.push({ id: 'q', label: `Suche: „${q}"`, onRemove: onClearQuery });
  // Label ohne Präfix (es ist kein Wert einer Dimension).
  if (untagged) chips.push({ id: 'untagged', label: 'Ohne Tags', onRemove: onClearUntagged });
  if (mine) chips.push({ id: 'mine', label: 'Meine Uploads', onRemove: onClearMine });

  if (chips.length === 0 && !anyActive) return null;

  return (
    <>
      {chips.map((chip) => (
        <span key={chip.id} className="inline-flex shrink-0">
          <span className={FILTER_CHIP_ACTIVE_LABEL}>
            <span className="truncate">{chip.label}</span>
          </span>
          <button
            type="button"
            onClick={chip.onRemove}
            aria-label={`Filter „${chip.label}" entfernen`}
            className={FILTER_CHIP_CLEAR}
          >
            <I.close size={14} />
          </button>
        </span>
      ))}
      {anyActive && (
        <button
          type="button"
          onClick={onClearAll}
          className="shrink-0 h-11 md:h-auto px-1 rounded text-xs font-semibold text-brand-green-700 underline-offset-2 hover:underline whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-green-700 focus-visible:ring-offset-2"
        >
          Alle Filter zurücksetzen
        </button>
      )}
    </>
  );
}
